/**
 * Undo/Redo System
 *
 * 40-deep snapshot stack with deduplication.
 * Ported from the monolith's undo system (legacy.html lines 3764-3806).
 */

import { captureSnapshot } from './snapshot.js';

const MAX_UNDO = 40;
let undoStack = [];
let redoStack = [];
let undoLock = false;

/**
 * Push the current state onto the undo stack.
 * Skips if locked (during undo/redo apply) or if state is identical
 * to the top of the stack.
 */
export function pushUndoState(canvas, state, size, mangleSeed = 0) {
  if (undoLock) return;
  const snap = captureSnapshot(canvas, state, size, mangleSeed);
  if (undoStack.length > 0) {
    const prev = undoStack[undoStack.length - 1];
    if (JSON.stringify(prev) === JSON.stringify(snap)) return;
  }
  undoStack.push(snap);
  if (undoStack.length > MAX_UNDO) undoStack.shift();
  redoStack.length = 0;
}

/**
 * Undo: restore the previous state.
 * @param {Function} applyCallback - receives the snapshot to apply
 */
export function undo(applyCallback) {
  if (undoStack.length < 2) return;
  redoStack.push(undoStack.pop());
  const prev = undoStack[undoStack.length - 1];
  undoLock = true;
  applyCallback(prev);
  undoLock = false;
}

/**
 * Redo: restore the next state.
 * @param {Function} applyCallback - receives the snapshot to apply
 */
export function redo(applyCallback) {
  if (redoStack.length === 0) return;
  const next = redoStack.pop();
  undoStack.push(next);
  undoLock = true;
  applyCallback(next);
  undoLock = false;
}

/** Clear both stacks (e.g., on template switch). */
export function clearHistory() {
  undoStack = [];
  redoStack = [];
}

export function canUndo() { return undoStack.length >= 2; }
export function canRedo() { return redoStack.length > 0; }
